import React from "react"
import { Dialog, DialogContent, Typography, Box, Grid, IconButton, useTheme } from "@mui/material"
import { styled } from "@mui/system"
import AppButton from "components/StyledComponents/AppButton"
import FieldsStepper from "pages/private/hotel/dynamic-forms/components/FieldsStepper"
import { ArrowBack, ArrowForward, Close } from "@mui/icons-material"
import { Formik } from "formik"
import { useDispatch } from "react-redux"
import { createNotableVipGroupAction, updateNotableVipGroupAction } from "redux/superAdmin/actions"
import VipGroupDynamicForm from "./VipGroupDynamicForm"
import * as Yup from "yup"
import { validationSchemaVipGroup } from "helpers/validationSchema"
import { useSelector } from "react-redux"
import { superAdminAction } from "redux/superAdmin/slice/user"
import { formatDate } from "helpers/functions"

const buttonText = {
  CANCEL: "Cancel",
  SAVE: "Save",
  UPDATE: "Update"
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.white.main,
    borderRadius: "8px"
  }
}))

const VipGroupFieldsModal = ({
  open,
  isEdit,
  onClose = () => {},
  dynamicFieldsGroup,
  groupDetails
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const formikRef = React.useRef()
  const [activeStep, setActiveStep] = React.useState(0)

  const { vipImageIds, notableGroupTypeId } = useSelector((state) => state.superAdmin)

  React.useEffect(() => {
    if (open) {
      dispatch(superAdminAction.resetVipImageIds())
      if (formikRef.current) {
        formikRef.current.resetForm()
        setActiveStep(0)
      }
    }
  }, [open])

  const stepTitles = [
    isEdit ? "Edit Basic Info" : "Add Basic Info",
    ...(dynamicFieldsGroup
      ?.filter((s) => {
        const typeMatch = ["image_scroll", "array"].includes(s.field_type?.toLowerCase())
        const hasSchema = Object.keys(s.field_schema || {}).length > 0
        return typeMatch && hasSchema
      })
      ?.map((s) => (isEdit ? `Edit ${s.field_label}` : `Add ${s.field_label}`)) || [])
  ]

  const style = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "15px",
      right: "15px",
      backgroundColor: theme.palette.background.main
    }
  }

  const handleClose = () => {
    onClose()
    setActiveStep(0)
    dispatch(superAdminAction.resetVipImageIds())
  }

  const handleSubmitForm = (values) => {
    const dynamic_field_data = {}
    const payload = {
      name: values.name,
      description: values.description,
      dates: values.dates,
      image: vipImageIds["image"] || values.image?.id || values.image || "",
      icon: vipImageIds["icon"] || values.icon?.id || values.icon || "",
      start_date: formatDate(values.startDate),
      end_date: formatDate(values.endDate),
      highlight_type: notableGroupTypeId
    }

    dynamicFieldsGroup?.forEach((section) => {
      const sectionKey = section?.field_name
      const schema = section?.field_schema || {}
      const sectionType = section?.field_type?.toLowerCase()

      if (sectionType === "image_scroll" || sectionType === "array") {
        dynamic_field_data[sectionKey] = (values[sectionKey] || []).map((item, itemIdx) => {
          const itemObj = {}
          Object.keys(schema).forEach((fieldKey) => {
            const fieldType = schema[fieldKey]?.toLowerCase()
            const formValue = item[fieldKey] ?? ""
            let val = formValue

            if (fieldType === "image" || fieldType === "file") {
              const fieldPath = `${sectionKey}[${itemIdx}].${fieldKey}`
              const reduxId = vipImageIds[fieldPath]
              val = reduxId || formValue?.id || formValue || ""
            }
            itemObj[fieldKey] = val
          })
          return itemObj
        })
      } else {
        const sectionObj = {}
        Object.keys(schema).forEach((fieldKey) => {
          const fieldType = schema[fieldKey]?.toLowerCase()
          const formValue = values?.[sectionKey]?.[fieldKey] ?? ""
          let val = formValue

          if (fieldType === "image" || fieldType === "file") {
            const fieldPath = `${sectionKey}.${fieldKey}`
            const reduxId = vipImageIds[fieldPath]
            val = reduxId || formValue?.id || formValue || ""
          }
          sectionObj[fieldKey] = val
        })
        dynamic_field_data[sectionKey] = [sectionObj]
      }
    })

    payload.dynamic_field_data = dynamic_field_data

    if (!isEdit) {
      dispatch(createNotableVipGroupAction({ ...payload, onSuccess: handleClose }))
    } else {
      dispatch(
        updateNotableVipGroupAction({ ...payload, id: groupDetails?.id, onSuccess: handleClose })
      )
    }
  }

  const generateInitialValues = () => {
    if (isEdit) {
      const initialValues = {
        name: groupDetails?.name || "",
        description: groupDetails?.description || "",
        dates: groupDetails?.dates || "",
        image: groupDetails?.image ? { id: groupDetails.image, url: groupDetails.image_url } : "",
        icon: groupDetails?.icon ? { id: groupDetails.icon, url: groupDetails.icon_url } : "",
        startDate: groupDetails?.start_date || null,
        endDate: groupDetails?.end_date || null
      }

      dynamicFieldsGroup?.forEach((section) => {
        const sectionKey = section?.field_name
        const sectionType = section?.field_type?.toLowerCase()
        const existingData = groupDetails?.dynamic_field_data?.[sectionKey]

        if (sectionType === "image_scroll" || sectionType === "array") {
          if (Array.isArray(existingData) && existingData.length > 0) {
            initialValues[sectionKey] = existingData.map((itemObj) => {
              const formattedItem = {}
              Object.keys(section.field_schema || {}).forEach((fieldKey) => {
                const fieldType = section.field_schema[fieldKey]?.toLowerCase()
                const fieldValue = itemObj[fieldKey]

                if ((fieldType === "image" || fieldType === "file") && fieldValue) {
                  formattedItem[fieldKey] = fieldValue
                } else {
                  formattedItem[fieldKey] = fieldValue ?? ""
                }
              })
              return formattedItem
            })
          } else {
            initialValues[sectionKey] = [{}]
          }
        } else {
          const itemObj =
            Array.isArray(existingData) && existingData.length > 0 ? existingData[0] : {}
          initialValues[sectionKey] = {}
          if (section?.field_schema) {
            Object.keys(section.field_schema).forEach((fieldKey) => {
              initialValues[sectionKey][fieldKey] = itemObj[fieldKey] || ""
            })
          }
        }
      })
      return initialValues
    }

    const initialValues = {
      name: "",
      description: "",
      dates: "",
      image: "",
      icon: "",
      startDate: null,
      endDate: null
    }

    dynamicFieldsGroup?.forEach((section) => {
      const sectionKey = section?.field_name
      const sectionType = section?.field_type?.toLowerCase()

      if (sectionType === "image_scroll" || sectionType === "array") {
        initialValues[sectionKey] = [{}]
      } else {
        initialValues[sectionKey] = {}
        if (section?.field_schema) {
          Object.keys(section.field_schema).forEach((fieldKey) => {
            initialValues[sectionKey][fieldKey] = ""
          })
        }
      }
    })

    return initialValues
  }

  const generateValidationSchema = () => {
    if (activeStep === 0) {
      return validationSchemaVipGroup
    }
    const section = dynamicFieldsGroup?.filter((s) => {
      const typeMatch = ["image_scroll", "array"].includes(s.field_type?.toLowerCase())
      const hasSchema = Object.keys(s.field_schema || {}).length > 0
      return typeMatch && hasSchema
    })?.[activeStep - 1]

    if (section) {
      const sectionKey = section.field_name
      const schema = section.field_schema || {}
      const itemSchema = {}

      Object.keys(schema).forEach((fieldKey) => {
        const fieldType = schema[fieldKey]?.toLowerCase()
        let validator = Yup.string()

        if (fieldType === "image" || fieldType === "file") {
          validator = Yup.mixed().optional()
        } else {
          validator = Yup.string().optional()
        }

        if (fieldKey.toLowerCase() === "bio") {
          validator = validator.max(150, "Bio cannot exceed 150 characters")
        }

        itemSchema[fieldKey] = validator
      })

      return Yup.object().shape({
        [sectionKey]: Yup.array().of(Yup.object().shape(itemSchema))
      })
    }
    return Yup.object().shape({})
  }

  const isLastStep = activeStep === stepTitles.length - 1

  const handleNext = async (formik) => {
    const errors = await formik.validateForm()
    if (Object.keys(errors).length === 0) {
      if (!isLastStep) {
        setActiveStep((prev) => prev + 1)
      }
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true
          return acc
        }, {})
      )
    }
  }

  const handlePrev = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1)
    }
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={generateInitialValues()}
      enableReinitialize={true}
      validationSchema={generateValidationSchema()}
      onSubmit={(values) => handleSubmitForm(values)}>
      {(formik) => {
        return (
          <StyledDialog open={open} maxWidth="md" fullWidth>
            <Box className="d-flex justify-content-end">
              <IconButton onClick={handleClose} sx={style.closeBtn}>
                <Close sx={{ height: "14px", width: "14px" }} />
              </IconButton>
            </Box>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h2" textAlign="center">
                    {isEdit ? "Edit Notable VIP Group" : "Add Notable VIP Group"}
                  </Typography>
                  <Typography variant="h5" textAlign="center" sx={{ mb: 2 }}>
                    {stepTitles[activeStep]}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FieldsStepper activeIndex={activeStep} rows={stepTitles.length} />
                </Grid>
                <Grid item xs={12}>
                  <VipGroupDynamicForm
                    dynamicFieldsGroup={dynamicFieldsGroup}
                    formik={formik}
                    activeStep={activeStep}
                  />
                </Grid>
              </Grid>
              <Box className="mt-5 d-flex justify-content-center" gap={4}>
                {activeStep > 0 && (
                  <AppButton
                    sx={{ padding: "10px 20px" }}
                    onClick={handlePrev}
                    startIcon={<ArrowBack />}>
                    Prev
                  </AppButton>
                )}
                {!isLastStep ? (
                  <AppButton
                    sx={{ padding: "10px 20px" }}
                    onClick={() => handleNext(formik)}
                    endIcon={<ArrowForward />}>
                    Next
                  </AppButton>
                ) : (
                  <AppButton sx={{ padding: "10px 50px" }} onClick={formik.handleSubmit}>
                    {isEdit ? buttonText.UPDATE : buttonText.SAVE}
                  </AppButton>
                )}
              </Box>
            </DialogContent>
          </StyledDialog>
        )
      }}
    </Formik>
  )
}

export default VipGroupFieldsModal
